import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, Compass, Sparkles, Library as LibIcon } from "lucide-react";
import { api } from "@/lib/api";
import { HOME_SECTIONS, IMAGES } from "@/data/content";
import { Reveal, Eyebrow, SectionTitle } from "@/components/common";

export default function Home() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.heroTemple} alt="Ancient Indic temple" className="h-full w-full object-cover animate-slow-zoom" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1021] via-[#0B1021]/85 to-[#0B1021]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1021] via-transparent to-[#0B1021]/60" />
        </div>
        <div className="relative mx-auto max-w-[1400px] w-full px-4 sm:px-8 pt-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} className="max-w-3xl">
            <Eyebrow>By Mahakali Tribunal · Intelligence · Justice · Wealth</Eyebrow>
            <h1 className="mt-6 font-display text-4xl sm:text-6xl lg:text-7xl font-bold tracking-[0.06em] text-amber-50 leading-none">INDIC CIVILISATION</h1>
            <p className="mt-6 font-serif text-2xl sm:text-3xl lg:text-4xl text-amber-100/90 italic leading-snug text-balance">
              A Civilisation of Knowledge, Inquiry &amp; Imagination
            </p>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-slate-300/90 leading-relaxed">
              Explore the histories, philosophies, traditions, sciences, and cultural achievements of the Indic world — a living digital institution, free to read. The Authentic People of Ancient India. The people who belong to India and vice versa.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/civilisation" className="group inline-flex items-center gap-2 rounded-sm bg-indic-gold px-7 py-3.5 font-sans font-semibold text-indic-indigo hover:bg-indic-gold-soft transition-colors" data-testid="explore-civilisation-btn">
                <Compass size={18} /> Explore Civilisation
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/library" className="inline-flex items-center gap-2 rounded-sm border border-indic-gold/50 px-7 py-3.5 font-sans font-semibold text-amber-50 hover:bg-indic-gold/10 transition-colors" data-testid="enter-library-btn">
                <LibIcon size={18} /> Enter the Library
              </Link>
              <Link to="/traditions" className="inline-flex items-center gap-2 rounded-sm px-5 py-3.5 font-sans font-medium text-slate-200 hover:text-indic-gold transition-colors" data-testid="discover-traditions-btn">
                Discover Traditions <ArrowRight size={15} />
              </Link>
            </div>
          </motion.div>

          {stats && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 1 }}
              className="mt-16 flex flex-wrap gap-x-12 gap-y-4 border-t border-indic-gold/15 pt-8 max-w-2xl" data-testid="hero-stats">
              {[["Great Works", stats.works], ["Sections & Chapters", stats.documents.toLocaleString()], ["Words of Scholarship", (stats.words / 1000000).toFixed(1) + "M"]].map(([l, v]) => (
                <div key={l}>
                  <div className="font-serif text-3xl text-indic-gold">{v}</div>
                  <div className="text-xs font-sans uppercase tracking-[0.2em] text-slate-400 mt-1">{l}</div>
                </div>
              ))}
            </motion.div>
          )}
        </div>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-slate-400 text-xs tracking-[0.3em] uppercase animate-pulse">Scroll to enter</div>
      </section>

      {/* INTRO STATEMENT */}
      <section className="relative py-24 lg:py-32 bg-indic-indigo">
        <div className="mx-auto max-w-4xl px-4 sm:px-8 text-center">
          <Reveal>
            <Eyebrow>The Premise</Eyebrow>
            <p className="mt-6 font-serif text-2xl sm:text-3xl lg:text-4xl text-amber-50/90 leading-snug text-balance">
              A civilisation is not a single race, state, or religion. It is a long-lived, internally plural field of knowledge —
              <span className="text-indic-gold"> preserved with care, read with freedom, and open to question.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* SECTIONS BENTO */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-b from-indic-indigo to-[#080c1a]">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8">
          <Reveal><SectionTitle eyebrow="Enter the Civilisation" title="Ten doorways into the Indic world" subtitle="Each a gateway to histories, ideas, and texts — rigorously sourced and free to explore." /></Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {HOME_SECTIONS.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.08}>
                <Link to={s.to} className="group relative block h-72 overflow-hidden rounded-sm border border-indic-card-border" data-testid={`home-section-${i}`}>
                  <img src={s.img} alt={s.title} className="absolute inset-0 h-full w-full object-cover opacity-55 transition-all duration-700 group-hover:opacity-70 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1021] via-[#0B1021]/60 to-transparent" />
                  <div className="absolute inset-0 flex flex-col justify-end p-6">
                    <h3 className="font-serif text-2xl font-semibold text-amber-50">{s.title}</h3>
                    <p className="mt-2 text-sm text-slate-300/90 leading-relaxed">{s.desc}</p>
                    <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-sans font-semibold uppercase tracking-[0.2em] text-indic-gold opacity-0 -translate-y-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0">
                      Enter <ArrowRight size={13} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SCHOLAR CTA */}
      <section className="relative py-24 lg:py-32 overflow-hidden bg-[#080c1a]">
        <img src={IMAGES.mandala} alt="" className="absolute right-0 top-1/2 -translate-y-1/2 w-[55%] opacity-10 object-cover" />
        <div className="relative mx-auto max-w-[1400px] px-4 sm:px-8">
          <div className="max-w-2xl">
            <Reveal>
              <span className="inline-flex items-center gap-2 text-indic-gold"><Sparkles size={18} /><Eyebrow>Knowledge Without Boundaries</Eyebrow></span>
              <h2 className="mt-5 font-serif text-4xl lg:text-5xl font-semibold text-amber-50 leading-tight">Ask INDIC Scholar</h2>
              <p className="mt-5 text-lg text-slate-300/90 leading-relaxed">
                An AI research assistant grounded in the INDIC library. Pose a question on philosophy, history, or a passage from the texts — and receive a sourced, scholarly answer that marks fact from interpretation.
              </p>
              <Link to="/scholar" className="mt-8 inline-flex items-center gap-2 rounded-sm bg-indic-gold px-7 py-3.5 font-sans font-semibold text-indic-indigo hover:bg-indic-gold-soft transition-colors" data-testid="home-scholar-cta">
                <BookOpen size={18} /> Begin a conversation <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* EDITORIAL NOTICE */}
      <section className="bg-[#080c1a] border-t border-indic-crimson/30 py-10" data-testid="home-notice">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8">
          <div className="rounded-sm border border-indic-crimson/40 bg-indic-crimson/10 px-6 py-5">
            <p className="text-sm sm:text-base font-sans font-medium text-red-300 leading-relaxed">
              <span className="font-semibold uppercase tracking-wide text-red-400">Notice:</span>{" "}
              We have ignored certain groups and sects that are epistemically false, and are likely a Political or Social Distortion which makes them ineligible for being a part of Indic Civilisation.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
